import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Employee } from '../../types';
import Button from '../Form/Button';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import { useBranches } from '../../hooks/useBranches';
import { useRanks } from '../../hooks/useRanks';
import { EmployeeBasicInfo } from './EmployeeFormSections';
import { EmployeePersonalInfo } from './EmployeeFormSections';
import { EmployeeEmploymentDetails } from './EmployeeFormSections';

interface EmployeeFormProps {
  employee?: Employee;
  onSubmit: (data: Partial<Employee>) => void;
  onCancel: () => void;
  userRole?: string;
}

const nationalityOptions = [
  { value: 'Egyptian', label: 'Egyptian' },
  { value: 'Saudi', label: 'Saudi' },
  { value: 'UAE', label: 'UAE' },
  { value: 'Indian', label: 'Indian' },
  { value: 'Pakistani', label: 'Pakistani' },
  { value: 'Filipino', label: 'Filipino' },
  { value: 'Other', label: 'Other' },
];

const documentTypeOptions = [
  { value: 'national_id', label: 'National ID (البطاقة الشخصية)' },
  { value: 'passport', label: 'Passport (جواز السفر)' },
  { value: 'seaman_book', label: "Seaman's Book (الجواز البحري)" },
  { value: 'certificate', label: 'Certificate (شهادة)' },
  { value: 'medical', label: 'Medical Document (وثيقة طبية)' },
  { value: 'other', label: 'Other (أخرى)' },
];

const EmployeeForm: React.FC<EmployeeFormProps> = ({ 
  employee, 
  onSubmit, 
  onCancel, 
  userRole = 'admin' 
}) => {
  const { data: branches = [] } = useBranches();
  const [nationalIdFileName, setNationalIdFileName] = useState<string>('');
  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [employeeNumber, setEmployeeNumber] = useState<string>('001');
  const [documentType, setDocumentType] = useState<string>('');
  
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(queryOptions);
  const { data: ranks = [] } = useRanks();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch
  } = useForm<Employee>({
    defaultValues: employee || {
      travelPermit: false,
      status: 'active',
      payType: 'monthly'
    }
  });

  const payType = watch('payType') || 'monthly';

  useEffect(() => {
    if (employee) {
      reset(employee);
      if (employee.code && codePrefixes.length > 0) {
        const matchingPrefix = codePrefixes.find((p: any) => 
          employee.code.startsWith(p.prefix)
        );
        if (matchingPrefix) {
          setSelectedCodePrefix(matchingPrefix.id);
          setEmployeeNumber(employee.code.replace(matchingPrefix.prefix, ''));
        }
      }
    }
  }, [employee, reset, codePrefixes]);

  const onFormSubmit = (data: Employee) => {
    onSubmit(data);
  };

  const branchOptions = (branches as any[]).map((branch: any) => ({
    value: branch.documentId || branch.id,
    label: branch.name
  }));

  const rankOptions = (ranks as any[]).map((rank: any) => ({
    value: rank.name,
    label: rank.name
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {employee ? 'Edit Employee' : 'Add New Employee'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <EmployeeBasicInfo
              register={register}
              errors={errors}
              setValue={setValue}
              selectedCodePrefix={selectedCodePrefix}
              setSelectedCodePrefix={setSelectedCodePrefix}
              employeeNumber={employeeNumber}
              setEmployeeNumber={setEmployeeNumber}
              codePrefixes={codePrefixes}
              rankOptions={rankOptions}
            />

            <EmployeePersonalInfo
              register={register}
              errors={errors}
              nationalIdFileName={nationalIdFileName}
              setNationalIdFileName={setNationalIdFileName}
              documentType={documentType}
              setDocumentType={setDocumentType}
              nationalityOptions={nationalityOptions}
              documentTypeOptions={documentTypeOptions}
            />

            <EmployeeEmploymentDetails
              register={register}
              errors={errors}
              userRole={userRole}
              branchOptions={branchOptions}
              payType={payType}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : employee ? 'Update Employee' : 'Add Employee'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeForm;
